import { useMemo } from "react";
import { useSelector } from "react-redux";

import { RESTRICTED_TECHNOLOGY_IDS } from "@components/WPTechnologyIcons/WPTechnologyIcons.constants";

import {
  LICENSE_FEATURES_PERMISSION,
  LICENSE_PRODUCTS_PERMISSION,
} from "@common/header/UserSettings/constants/UserSettings.constants";

const TECH_LICENSE_STATUS = {
  active: "active",
  expired: "expired",
  none: "none",
};

const useUserDetails = () => {
  const { data: UserInfoStore } = useSelector(state => state.userInfo);
  const { langauage, userPermissions, expiredTechPermissions, expiredProductPermissions } = UserInfoStore || {};

  const isExpElementAccess = useMemo(
    () => userPermissions?.Feature?.includes(LICENSE_FEATURES_PERMISSION.EXPERIMENTAL_ELEMENT),
    [userPermissions],
  );

  const hasMLDAccess = useMemo(
    () => userPermissions?.Feature?.includes(LICENSE_FEATURES_PERMISSION.MLD),
    [userPermissions],
  );

  const isMldLicenseExpired = useMemo(
    () => !hasMLDAccess && Boolean(expiredProductPermissions?.includes(LICENSE_FEATURES_PERMISSION.MLD)),
    [hasMLDAccess, expiredProductPermissions],
  );

  const hasLithiumAccess = useMemo(
    () => userPermissions?.Product?.includes(LICENSE_PRODUCTS_PERMISSION.LITHIUM),
    [userPermissions],
  );

  const isLithiumLicenseExpired = useMemo(
    () => !hasLithiumAccess && Boolean(expiredProductPermissions?.includes(LICENSE_PRODUCTS_PERMISSION.LITHIUM)),
    [hasLithiumAccess, expiredProductPermissions],
  );

  const hasPitzerAccess = useMemo(
    () => userPermissions?.Feature?.includes(LICENSE_FEATURES_PERMISSION.PITZER),
    [userPermissions],
  );

  const isISDFactorAccess = useMemo(
    () => userPermissions?.Feature?.includes(LICENSE_FEATURES_PERMISSION.CUSTOMIZATION_FACTORS),
    [userPermissions],
  );

  const checkTechnologyAccess = techName => {
    const { Technology } = userPermissions || {};

    return Technology?.some(technology => technology === techName);
  };

  /**
   * Returns the license status for a given technology name.
   * - "active"  — user has a valid, non-expired license for this technology
   * - "expired" — user's license for this technology has expired
   * - "none"    — user has no license for this technology at all
   */
  const getTechnologyLicenseStatus = techName => {
    const { Technology } = userPermissions || {};

    if (expiredTechPermissions?.includes(techName)) return TECH_LICENSE_STATUS.expired;
    if (Technology?.includes(techName)) return TECH_LICENSE_STATUS.active;
    return TECH_LICENSE_STATUS.none;
  };

  const isRestrictedTechExpired = treatmentId =>
    Boolean(treatmentId) &&
    RESTRICTED_TECHNOLOGY_IDS.has(treatmentId) &&
    getTechnologyLicenseStatus(treatmentId) !== TECH_LICENSE_STATUS.active;

  return {
    langauage,
    hasMLDAccess,
    hasPitzerAccess,
    isISDFactorAccess,
    isExpElementAccess,
    isMldLicenseExpired,
    hasLithiumAccess,
    isLithiumLicenseExpired,
    checkTechnologyAccess,
    getTechnologyLicenseStatus,
    isRestrictedTechExpired,
  };
};

export default useUserDetails;
